import { gsap } from 'gsap';

// BARCINO - Page torrefacteurs : hover images + drawer regions
const barcinoTorrefacteurs = {
    _row_handlers: [],
    _view_handlers: [],
    _region_handlers: [],
    _close_handlers: [],
    _key_handler: null,
    _switch_timeout: null,
    _hover_intent_timeout: null,
    _animation_frame: null,
    _hover_x_to: null,
    _hover_y_to: null,
    _hover_reveal_tween: null,
    _hover_show_tween: null,
    _hover_anchor: null,
    _pending_pointer: null,
    _is_revealing: false,
    _active_hover_layer: 0,
    _active_roaster_image: '',
    _active_roaster_index: null,
    _last_requested_roaster_index: null,
    _last_pointer: null,
    _pointer_direction: {
        x: 'right',
        y: 'up',
    },

    init() {
        this.destroy();

        const rows = document.querySelectorAll('[data-roaster-row]');
        const listWrap = document.querySelector('[data-roasters-list]');
        const hover = document.querySelector('[data-roaster-hover]');
        const hoverLayers = hover ? Array.from(hover.querySelectorAll('[data-roaster-hover-layer]')) : [];
        const hoverImgs = hoverLayers
            .map((layer) => layer.querySelector('[data-roaster-hover-img]'))
            .filter(Boolean);
        const clamp = gsap.utils.clamp;
        const clampX = clamp(-18, 18);
        const clampY = clamp(-12, 12);

        const clearMotionClasses = () => {
            if (!hover) return;
            hover.classList.remove('is-switch-up', 'is-switch-down');
        };

        const resetHoverMotion = () => {
            if (!hover) return;
            gsap.killTweensOf(hover);
            gsap.set(hover, {
                x: 0,
                y: 18,
                scale: .985,
                rotation: 6,
                autoAlpha: 0,
                transformOrigin: 'center center',
            });
            this._hover_x_to = gsap.quickTo(hover, 'x', { duration: .38, ease: 'power3.out' });
            this._hover_y_to = gsap.quickTo(hover, 'y', { duration: .38, ease: 'power3.out' });
        };

        const resetLayerMotion = () => {
            if (hoverLayers.length === 0) return;
            gsap.killTweensOf(hoverLayers);
            gsap.set(hoverLayers, {
                autoAlpha: 0,
                clipPath: 'inset(100% 0 0 0)',
                zIndex: 0,
            });
            if (hoverImgs.length > 0) {
                gsap.killTweensOf(hoverImgs);
                gsap.set(hoverImgs, { scale: 1, y: 0 });
            }
        };

        const updatePointerDirection = (event) => {
            if (!event) return;

            if (this._last_pointer) {
                const deltaX = event.clientX - this._last_pointer.x;
                const deltaY = event.clientY - this._last_pointer.y;

                if (Math.abs(deltaX) > 2) {
                    this._pointer_direction.x = deltaX < 0 ? 'left' : 'right';
                }

                if (Math.abs(deltaY) > 2) {
                    this._pointer_direction.y = deltaY < 0 ? 'up' : 'down';
                }
            }

            this._last_pointer = {
                x: event.clientX,
                y: event.clientY,
            };
        };

        const updateTracking = (event) => {
            if (!hover || !event) return;
            if (this._is_revealing) {
                this._pending_pointer = {
                    x: event.clientX,
                    y: event.clientY,
                };
                return;
            }

            if (!this._hover_anchor) {
                this._hover_anchor = {
                    x: event.clientX,
                    y: event.clientY,
                };
            }

            const targetX = clampX((event.clientX - this._hover_anchor.x) * .16);
            const targetY = clampY((event.clientY - this._hover_anchor.y) * .14);

            this._hover_x_to?.(targetX);
            this._hover_y_to?.(targetY);
        };

        const setLayerMedia = (layer, row) => {
            const img = layer.querySelector('[data-roaster-hover-img]');
            const webp = layer.querySelector('[data-roaster-hover-webp]');
            const large = row.getAttribute('data-image-large') || '';

            if (!img || large === '') return false;

            img.setAttribute('src', large);
            img.setAttribute('alt', row.getAttribute('data-image-alt') || '');

            if (webp) {
                webp.setAttribute('srcset', row.getAttribute('data-image-webp') || '');
            }

            return true;
        };

        const clearHoverIntent = () => {
            window.clearTimeout(this._hover_intent_timeout);
            this._hover_intent_timeout = null;
        };

        const getRowIndex = (row) => Array.prototype.indexOf.call(rows, row);

        const getRevealDirection = (rowIndex) => {
            if (rowIndex === -1) return this._pointer_direction.y;

            const previousIndex = this._last_requested_roaster_index !== null
                ? this._last_requested_roaster_index
                : this._active_roaster_index;

            if (previousIndex === null || previousIndex === rowIndex) {
                return this._pointer_direction.y;
            }

            return rowIndex > previousIndex ? 'down' : 'up';
        };

        const scheduleRowActivation = (row, event) => {
            clearHoverIntent();
            updatePointerDirection(event);

            const rowIndex = getRowIndex(row);
            const revealDirection = getRevealDirection(rowIndex);
            if (rowIndex !== -1) {
                this._last_requested_roaster_index = rowIndex;
            }

            const pointer = event ? {
                clientX: event.clientX,
                clientY: event.clientY,
            } : null;

            if (pointer) {
                this._hover_anchor = {
                    x: pointer.clientX,
                    y: pointer.clientY,
                };
            }

            this._hover_intent_timeout = window.setTimeout(() => {
                this._hover_intent_timeout = null;
                activateRow(row, revealDirection);
                if (pointer) {
                    updateTracking(pointer);
                }
            }, 0);
        };

        resetHoverMotion();
        resetLayerMotion();

        const activateRow = (row, forcedRevealDirection = null) => {
            rows.forEach((item) => item.classList.toggle('is-active', item === row));
            if (!hover || hoverLayers.length === 0) return;

            window.clearTimeout(this._switch_timeout);
            if (this._animation_frame) {
                window.cancelAnimationFrame(this._animation_frame);
                this._animation_frame = null;
            }
            this._hover_reveal_tween?.kill();
            this._hover_show_tween?.kill();
            gsap.killTweensOf(hoverLayers);
            gsap.killTweensOf(hoverImgs);

            const large = row.getAttribute('data-image-large') || '';
            if (large === '') {
                hover.classList.remove('is-visible');
                this._hover_show_tween = gsap.to(hover, {
                    autoAlpha: 0,
                    duration: .2,
                    ease: 'power2.out',
                });
                hover.setAttribute('aria-hidden', 'true');
                return;
            }

            const sameImage = this._active_roaster_image === large;
            const rowIndex = getRowIndex(row);
            let revealDirection = forcedRevealDirection || this._pointer_direction.y;
            if (this._active_roaster_index !== null && rowIndex !== -1 && rowIndex !== this._active_roaster_index) {
                revealDirection = forcedRevealDirection || (rowIndex > this._active_roaster_index ? 'down' : 'up');
            }
            const nextLayerIndex = sameImage ? this._active_hover_layer : (this._active_hover_layer === 0 ? 1 : 0);
            const activeLayer = hoverLayers[this._active_hover_layer] || hoverLayers[0];
            const nextLayer = hoverLayers[nextLayerIndex] || hoverLayers[0];
            this._is_revealing = !sameImage;

            hover.setAttribute('aria-hidden', 'false');
            clearMotionClasses();
            hover.classList.add(revealDirection === 'down' ? 'is-switch-down' : 'is-switch-up');

            if (!sameImage && !setLayerMedia(nextLayer, row)) return;

            hoverLayers.forEach((layer) => {
                layer.classList.remove('is-active', 'is-previous', 'is-entering');
            });

            if (!sameImage) {
                activeLayer.classList.add('is-previous');
                nextLayer.classList.add('is-entering');
            }

            this._active_hover_layer = nextLayerIndex;
            this._active_roaster_image = large;
            if (rowIndex !== -1) {
                this._active_roaster_index = rowIndex;
                this._last_requested_roaster_index = rowIndex;
            }

            hover.classList.add('is-visible');
            if (!sameImage) {
                gsap.killTweensOf(hover, 'x,y');
                this._hover_x_to = null;
                this._hover_y_to = null;
            }
            const showVars = {
                autoAlpha: 1,
                y: 0,
                scale: 1,
                rotation: 6,
                duration: .08,
                ease: 'power3.out',
                overwrite: 'auto',
            };
            if (!sameImage) {
                showVars.x = 0;
            }
            this._hover_show_tween = gsap.to(hover, showVars);

            const revealClip = revealDirection === 'down'
                ? 'inset(0 0 100% 0)'
                : 'inset(100% 0 0 0)';

            gsap.set(activeLayer, {
                autoAlpha: 1,
                clipPath: 'inset(0 0 0% 0)',
                zIndex: 10,
            });
            gsap.set(nextLayer, {
                autoAlpha: 1,
                clipPath: sameImage ? 'inset(0 0 0% 0)' : revealClip,
                zIndex: 20,
            });

            nextLayer.classList.add('is-active');

            const revealDuration = sameImage ? 0 : .08;
            const nextImg = nextLayer.querySelector('[data-roaster-hover-img]');

            if (nextImg && !sameImage) {
                gsap.fromTo(nextImg,
                    { scale: 1.06, y: revealDirection === 'down' ? -14 : 14 },
                    { scale: 1, y: 0, duration: revealDuration, ease: 'power3.out', overwrite: true }
                );
            }

            this._hover_reveal_tween = gsap.to(nextLayer, {
                clipPath: 'inset(0 0 0% 0)',
                duration: revealDuration,
                ease: 'power3.out',
                overwrite: true,
                onComplete: () => {
                    gsap.set(nextLayer, { clipPath: 'inset(0% 0% 0% 0%)', autoAlpha: 1, zIndex: 20 });
                    hover.classList.remove('is-switch-up', 'is-switch-down');
                    nextLayer.classList.remove('is-entering');
                    activeLayer.classList.remove('is-previous');
                    gsap.set(activeLayer, { autoAlpha: 0, clipPath: 'inset(100% 0 0 0)', zIndex: 0 });
                    this._is_revealing = false;
                    this._hover_anchor = this._pending_pointer || this._last_pointer;
                    this._pending_pointer = null;
                    this._hover_x_to = gsap.quickTo(hover, 'x', { duration: .38, ease: 'power3.out' });
                    this._hover_y_to = gsap.quickTo(hover, 'y', { duration: .38, ease: 'power3.out' });
                    this._hover_reveal_tween = null;
                },
            });

            this._switch_timeout = window.setTimeout(() => {
                this._switch_timeout = null;
            }, 1000);
        };

        rows.forEach((row) => {
            const enter = (event) => {
                scheduleRowActivation(row, event);
            };
            const focus = () => activateRow(row);
            row.addEventListener('mouseenter', enter);
            row.addEventListener('focus', focus);
            this._row_handlers.push({ el: row, type: 'mouseenter', fn: enter });
            this._row_handlers.push({ el: row, type: 'focus', fn: focus });
        });

        if (listWrap && hover) {
            const leave = () => {
                clearHoverIntent();
                rows.forEach((item) => item.classList.remove('is-active'));
                hover.classList.remove('is-visible');
                clearMotionClasses();
                hover.setAttribute('aria-hidden', 'true');
                this._hover_reveal_tween?.kill();
                this._hover_reveal_tween = null;
                this._is_revealing = false;
                this._hover_show_tween?.kill();
                this._hover_show_tween = gsap.to(hover, {
                    autoAlpha: 0,
                    duration: .22,
                    ease: 'power2.out',
                });
                this._last_pointer = null;
                this._hover_anchor = null;
                // Reset de l'état "image active" : une fois masqué, le prochain survol
                // (même de la même ligne) doit rejouer l'animation d'entrée complète,
                // sinon la branche "sameImage" ne refait pas réapparaître l'image.
                this._active_roaster_image = '';
                this._active_roaster_index = null;
                this._last_requested_roaster_index = null;
            };

            const move = (event) => {
                updatePointerDirection(event);
                if (!hover.classList.contains('is-visible')) return;
                updateTracking(event);
            };

            listWrap.addEventListener('mouseleave', leave);
            listWrap.addEventListener('mousemove', move);
            this._row_handlers.push({ el: listWrap, type: 'mouseleave', fn: leave });
            this._row_handlers.push({ el: listWrap, type: 'mousemove', fn: move });
        }

        const viewRoot = document.querySelector('[data-roasters-view-root]');
        const viewToggle = document.querySelector('[data-roasters-view-toggle]');
        const list = document.querySelector('[data-roasters-list]');
        const grid = document.querySelector('[data-roasters-grid]');

        const setView = (view) => {
            if (!viewRoot) return;
            const isGrid = view === 'grid';

            viewRoot.classList.toggle('is-grid-view', isGrid);
            viewRoot.classList.toggle('is-list-view', !isGrid);
            list?.setAttribute('aria-hidden', String(isGrid));
            grid?.setAttribute('aria-hidden', String(!isGrid));

            if (!viewToggle) return;

            const targetView = isGrid ? 'list' : 'grid';
            const targetLabel = targetView === 'list'
                ? viewToggle.dataset.viewListLabel
                : viewToggle.dataset.viewGridLabel;
            const label = viewToggle.querySelector('span');

            viewToggle.setAttribute('data-roasters-view-target', targetView);
            viewToggle.setAttribute('aria-label', targetLabel || '');
            if (label) label.textContent = targetLabel || '';
        };

        if (viewRoot) {
            // Vue grille par défaut sur mobile uniquement (liste peu lisible en
            // dessous de middle-screen) — le bouton reste disponible pour basculer.
            const isMobile = window.matchMedia('(max-width: 768px)').matches;
            setView(isMobile ? 'grid' : 'list');
        }

        if (viewToggle) {
            const handler = () => setView(viewToggle.getAttribute('data-roasters-view-target') || 'grid');
            viewToggle.addEventListener('click', handler);
            this._view_handlers.push({ el: viewToggle, type: 'click', fn: handler });
        }

        const drawer = document.querySelector('[data-region-drawer]');
        const scrollArea = drawer ? drawer.querySelector('.bc-region-drawer__panel') : null;
        const triggers = document.querySelectorAll('[data-region-trigger]');
        const panels = document.querySelectorAll('[data-region-panel]');
        const closeButtons = document.querySelectorAll('[data-region-close]');

        const openRegion = (regionId) => {
            if (!drawer || !regionId) return;
            drawer.classList.add('is-open');
            drawer.setAttribute('aria-hidden', 'false');
            document.documentElement.classList.add('is-region-drawer-open');
            document.body.classList.add('is-region-drawer-open');

            triggers.forEach((trigger) => {
                trigger.classList.toggle('is-active', trigger.getAttribute('data-region-id') === regionId);
            });
            panels.forEach((panel) => {
                panel.classList.toggle('is-active', panel.getAttribute('data-region-panel') === regionId);
            });

            // Toujours (re)commencer la lecture en haut : le conteneur de défilement
            // est partagé entre toutes les régions, il conserverait sinon la position
            // de scroll de la région précédemment consultée.
            if (scrollArea) scrollArea.scrollTop = 0;
        };

        const closeRegion = () => {
            if (!drawer) return;
            drawer.classList.remove('is-open');
            drawer.setAttribute('aria-hidden', 'true');
            document.documentElement.classList.remove('is-region-drawer-open');
            document.body.classList.remove('is-region-drawer-open');
        };

        triggers.forEach((trigger) => {
            const handler = () => openRegion(trigger.getAttribute('data-region-id'));
            trigger.addEventListener('click', handler);
            this._region_handlers.push({ el: trigger, type: 'click', fn: handler });
        });

        closeButtons.forEach((button) => {
            button.addEventListener('click', closeRegion);
            this._close_handlers.push({ el: button, type: 'click', fn: closeRegion });
        });

        if (drawer) {
            const overlayClick = (event) => {
                if (event.target === drawer) closeRegion();
            };
            drawer.addEventListener('click', overlayClick);
            this._close_handlers.push({ el: drawer, type: 'click', fn: overlayClick });
        }

        this._key_handler = (event) => {
            if (event.key === 'Escape') closeRegion();
        };
        document.addEventListener('keydown', this._key_handler);
    },

    destroy() {
        this._row_handlers.forEach(({ el, type, fn }) => el.removeEventListener(type, fn));
        this._view_handlers.forEach(({ el, type, fn }) => el.removeEventListener(type, fn));
        this._region_handlers.forEach(({ el, type, fn }) => el.removeEventListener(type, fn));
        this._close_handlers.forEach(({ el, type, fn }) => el.removeEventListener(type, fn));
        this._row_handlers = [];
        this._view_handlers = [];
        this._region_handlers = [];
        this._close_handlers = [];

        if (this._key_handler) {
            document.removeEventListener('keydown', this._key_handler);
            this._key_handler = null;
        }

        window.clearTimeout(this._switch_timeout);
        window.clearTimeout(this._hover_intent_timeout);
        this._switch_timeout = null;
        this._hover_intent_timeout = null;
        this._hover_reveal_tween?.kill();
        this._hover_show_tween?.kill();
        this._hover_reveal_tween = null;
        this._hover_show_tween = null;
        if (this._animation_frame) {
            window.cancelAnimationFrame(this._animation_frame);
            this._animation_frame = null;
        }
        this._hover_x_to = null;
        this._hover_y_to = null;
        this._hover_anchor = null;
        this._pending_pointer = null;
        this._is_revealing = false;
        this._last_pointer = null;
        this._active_hover_layer = 0;
        this._active_roaster_image = '';
        this._active_roaster_index = null;
        this._last_requested_roaster_index = null;
        document.documentElement.classList.remove('is-region-drawer-open');
        document.body.classList.remove('is-region-drawer-open');
    },
};

export default barcinoTorrefacteurs;
